import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Plus, 
  Edit, 
  Trash2,
  Loader2, 
  FileText,
  CheckCircle,
  XCircle,
  AlertCircle,
  MessageSquare,
  Mail,
  Sparkles,
  ClipboardList,
  Copy,
  Eye,
  ArrowRight
} from 'lucide-react';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import QuickNoteGroupsCard from '../components/QuickNoteGroupsCard';
import { EmailDesignSelect, useEmailDesignOptions } from '../components/EmailDesignSelect';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TEMPLATE_CATEGORIES = [
  { id: 'case_notes', name: 'Case Notes', icon: ClipboardList, description: 'Templates for case documentation' },
  { id: 'email', name: 'Email', icon: Mail, description: 'Email communication templates' },
  { id: 'sms', name: 'Text Message', icon: MessageSquare, description: 'SMS message templates' },
  { id: 'prompts', name: 'AI Prompts', icon: Sparkles, description: 'AI generation prompts' },
];

const TemplatesPage = () => {
  const [templates, setTemplates] = useState([]);
  const [mergeFields, setMergeFields] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showPreviewDialog, setShowPreviewDialog] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [deletingTemplate, setDeletingTemplate] = useState(null);
  const [previewTemplate, setPreviewTemplate] = useState(null);
  const [saving, setSaving] = useState(false);
  const [activeCategory, setActiveCategory] = useState('case_notes');
  const [searchTerm, setSearchTerm] = useState('');
  const [noteTypes, setNoteTypes] = useState([]);
  const [caseTypes, setCaseTypes] = useState([]);
  const [cursorPosition, setCursorPosition] = useState(0);  // Track cursor position
  const bodyTextareaRef = useRef(null);  // Ref for the body textarea
  
  const [formData, setFormData] = useState({
    name: '',
    category: 'case_notes',
    subject: '',
    body: '',
    description: '',
    ai_prompt: '',
    is_active: true,
    default_note_type: '',
    case_types: [],
    email_design_id: null
  });
  const emailDesigns = useEmailDesignOptions();

  useEffect(() => {
    fetchTemplates();
    fetchMergeFields();
    fetchNoteTypes();
    fetchCaseTypes();
  }, []);

  const fetchCaseTypes = async () => {
    try {
      const response = await axios.get(`${API}/admin/templates/case-types`);
      setCaseTypes(response.data.case_types || []);
    } catch (error) {
      console.error('Failed to fetch case types:', error);
      setCaseTypes([]);
    }
  };

  const fetchNoteTypes = async () => {
    try {
      const response = await axios.get(`${API}/dropdowns/note_types`);
      if (response.data.items && response.data.items.length > 0) {
        setNoteTypes(response.data.items);
      } else {
        setNoteTypes([
          { value: 'progress', label: 'Progress Note' },
          { value: 'intake', label: 'Intake Assessment' },
          { value: 'crisis', label: 'Crisis Intervention' },
          { value: 'follow_up', label: 'Follow-up' },
          { value: 'referral', label: 'Referral' },
        ]);
      }
    } catch (error) {
      console.error('Failed to fetch note types:', error);
      setNoteTypes([
        { value: 'progress', label: 'Progress Note' },
        { value: 'intake', label: 'Intake Assessment' },
        { value: 'crisis', label: 'Crisis Intervention' },
        { value: 'follow_up', label: 'Follow-up' },
        { value: 'referral', label: 'Referral' },
      ]);
    }
  };

  const fetchMergeFields = async () => {
    try {
      const response = await axios.get(`${API}/case-note-templates/merge-fields`);
      setMergeFields(response.data);
    } catch (error) {
      console.error('Failed to fetch merge fields:', error);
      // Fallback to basic fields if API fails
      setMergeFields([
        { field: '{{participant_full_name}}', description: 'Participant full name' },
        { field: '{{participant_phone}}', description: 'Participant phone number' },
        { field: '{{participant_email}}', description: 'Participant email' },
        { field: '{{case_manager_name}}', description: 'Case manager name' },
        { field: '{{today_date}}', description: 'Today\'s date' },
      ]);
    }
  };

  const fetchTemplates = async () => {
    try {
      const response = await axios.get(`${API}/admin/templates`);
      setTemplates(response.data.templates || []);
    } catch (error) {
      toast.error('Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (template = null) => {
    if (template) {
      setEditingTemplate(template);
      setFormData({
        name: template.name || '',
        category: template.category || 'case_notes',
        subject: template.subject || '',
        body: template.body || '',
        description: template.description || '',
        ai_prompt: template.ai_prompt || '',
        is_active: template.is_active !== false,
        default_note_type: template.default_note_type || '',
        case_types: template.case_types || [],
        email_design_id: template.email_design_id || null
      });
    } else {
      setEditingTemplate(null);
      setFormData({
        name: '',
        category: activeCategory,
        subject: '',
        body: '',
        description: '',
        ai_prompt: '',
        is_active: true,
        default_note_type: '',
        case_types: [],
        email_design_id: null
      });
    }
    setShowDialog(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      if (editingTemplate) {
        await axios.put(`${API}/admin/templates/${editingTemplate.id}`, formData);
        toast.success('Template updated successfully');
      } else {
        await axios.post(`${API}/admin/templates`, formData);
        toast.success('Template created successfully');
      }
      setShowDialog(false);
      fetchTemplates();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deletingTemplate) return;
    setSaving(true);

    try {
      await axios.delete(`${API}/admin/templates/${deletingTemplate.id}`);
      toast.success('Template deleted successfully');
      setShowDeleteDialog(false);
      setDeletingTemplate(null);
      fetchTemplates();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete template');
    } finally {
      setSaving(false);
    }
  };

  const handleDuplicate = async (template) => {
    try {
      await axios.post(`${API}/admin/templates`, {
        ...template,
        name: `${template.name} (Copy)`,
        id: undefined
      });
      toast.success('Template duplicated');
      fetchTemplates();
    } catch (error) {
      toast.error('Failed to duplicate template');
    }
  };

  const insertMergeField = (field) => {
    const textarea = bodyTextareaRef.current;
    if (!textarea) {
      // Fallback: append to end if ref not available
      setFormData(prev => ({
        ...prev,
        body: prev.body + field
      }));
      return;
    }
    
    // Get the current cursor position (or use stored position)
    const start = textarea.selectionStart ?? cursorPosition;
    const end = textarea.selectionEnd ?? cursorPosition;
    const currentBody = formData.body;
    
    // Insert field at cursor position
    const newBody = currentBody.substring(0, start) + field + currentBody.substring(end);
    
    setFormData(prev => ({
      ...prev,
      body: newBody
    }));
    
    // Set cursor position after the inserted field
    const newCursorPos = start + field.length;
    setCursorPosition(newCursorPos);
    
    // Focus textarea and set cursor position after state update
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(newCursorPos, newCursorPos);
    }, 0);
  };

  // Track cursor position when user clicks or types in textarea
  const handleBodyChange = (e) => {
    setFormData(prev => ({ ...prev, body: e.target.value }));
    setCursorPosition(e.target.selectionStart);
  };

  const handleBodySelect = (e) => {
    setCursorPosition(e.target.selectionStart);
  };

  const filteredTemplates = templates.filter(t => 
    t.category === activeCategory &&
    (t.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
     t.description?.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const getCategoryIcon = (categoryId) => {
    const category = TEMPLATE_CATEGORIES.find(c => c.id === categoryId);
    return category?.icon || FileText;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]" data-testid="templates-loading">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="templates-page">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <FileText className="w-8 h-8 text-primary" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Templates</h1>
            <p className="text-gray-500">Manage templates for notes, emails, SMS, and AI prompts</p>
          </div>
        </div>
        <Button onClick={() => handleOpenDialog()} data-testid="add-template-btn">
          <Plus className="w-4 h-4 mr-2" />
          Add Template
        </Button>
      </div>

      <QuickNoteGroupsCard />

      <Tabs value={activeCategory} onValueChange={setActiveCategory}>
        <div className="flex items-center justify-between mb-4">
          <TabsList>
            {TEMPLATE_CATEGORIES.map((cat) => {
              const Icon = cat.icon;
              const count = templates.filter(t => t.category === cat.id).length;
              return (
                <TabsTrigger key={cat.id} value={cat.id} className="flex items-center gap-2">
                  <Icon className="w-4 h-4" />
                  {cat.name}
                  <Badge variant="secondary" className="ml-1">{count}</Badge>
                </TabsTrigger>
              );
            })}
          </TabsList>
          <Input
            placeholder="Search templates..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-64"
          />
        </div>

        {TEMPLATE_CATEGORIES.map((cat) => (
          <TabsContent key={cat.id} value={cat.id}>
            <Card>
              <CardHeader>
                <CardTitle>{cat.name} Templates</CardTitle>
                <CardDescription>{cat.description}</CardDescription>
              </CardHeader>
              <CardContent>
                {filteredTemplates.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <cat.icon className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>No {cat.name.toLowerCase()} templates found</p>
                    <Button 
                      variant="outline" 
                      className="mt-3"
                      onClick={() => handleOpenDialog()}
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Create First Template
                    </Button>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {filteredTemplates.map((template) => {
                      const Icon = getCategoryIcon(template.category);
                      return (
                        <Card key={template.id} className="border" data-testid={`template-card-${template.id}`}>
                          <CardHeader className="pb-2">
                            <div className="flex items-start justify-between">
                              <div className="flex items-center gap-2">
                                <Icon className="w-4 h-4 text-gray-500" />
                                <CardTitle className="text-base">{template.name}</CardTitle>
                              </div>
                              <Badge variant={template.is_active ? "default" : "secondary"}>
                                {template.is_active ? 'Active' : 'Inactive'}
                              </Badge>
                            </div>
                            {template.description && (
                              <CardDescription className="text-xs">{template.description}</CardDescription>
                            )}
                          </CardHeader>
                          <CardContent>
                            <p className="text-sm text-gray-600 line-clamp-2 mb-3">
                              {template.body?.substring(0, 100)}...
                            </p>
                            <div className="flex items-center gap-1">
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => {
                                  setPreviewTemplate(template);
                                  setShowPreviewDialog(true);
                                }}
                              >
                                <Eye className="w-4 h-4" />
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => handleDuplicate(template)}
                              >
                                <Copy className="w-4 h-4" />
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                onClick={() => handleOpenDialog(template)}
                              >
                                <Edit className="w-4 h-4" />
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="sm"
                                className="text-red-600"
                                onClick={() => {
                                  setDeletingTemplate(template);
                                  setShowDeleteDialog(true);
                                }}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        ))}
      </Tabs>

      {/* Add/Edit Template Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingTemplate ? 'Edit Template' : 'Create Template'}</DialogTitle>
            <DialogDescription>
              Configure template content and settings
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name">Template Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  placeholder="Initial Assessment Note"
                  required
                  data-testid="template-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="category">Category *</Label>
                <Select
                  value={formData.category}
                  onValueChange={(value) => setFormData({...formData, category: value})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {TEMPLATE_CATEGORIES.map((cat) => (
                      <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Input
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                placeholder="Brief description of this template"
              />
            </div>

            {/* Case Types Multi-Select - Only for case_notes category */}
            {formData.category === 'case_notes' && (
              <div className="space-y-2">
                <Label>Case Type(s)</Label>
                <p className="text-xs text-muted-foreground mb-2">
                  Select which case/enrollment types this template applies to. Leave empty for universal (all types).
                </p>
                <div className="border rounded-md p-3 max-h-48 overflow-y-auto bg-gray-50/50">
                  {caseTypes.length > 0 ? (
                    <div className="space-y-2">
                      {caseTypes.map((ct) => (
                        <label 
                          key={ct.id} 
                          className="flex items-center gap-2 text-sm cursor-pointer hover:bg-white p-1 rounded"
                        >
                          <input
                            type="checkbox"
                            checked={(formData.case_types || []).includes(ct.id)}
                            onChange={(e) => {
                              const currentTypes = formData.case_types || [];
                              if (e.target.checked) {
                                setFormData({...formData, case_types: [...currentTypes, ct.id]});
                              } else {
                                setFormData({...formData, case_types: currentTypes.filter(id => id !== ct.id)});
                              }
                            }}
                            className="rounded border-gray-300"
                          />
                          <span>{ct.name}</span>
                        </label>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">No case types available</p>
                  )}
                </div>
                {(formData.case_types || []).length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-2">
                    {(formData.case_types || []).map(ctId => {
                      const ct = caseTypes.find(c => c.id === ctId);
                      return ct ? (
                        <Badge key={ctId} variant="secondary" className="text-xs">
                          {ct.name}
                        </Badge>
                      ) : null;
                    })}
                  </div>
                )}
              </div>
            )}

            {(formData.category === 'email') && (
              <div className="space-y-2">
                <Label htmlFor="subject">Email Subject</Label>
                <Input
                  id="subject"
                  value={formData.subject}
                  onChange={(e) => setFormData({...formData, subject: e.target.value})}
                  placeholder="Subject line for emails"
                />
              </div>
            )}

            {(formData.category === 'email') && (
              <div className="space-y-2" data-testid="template-email-design-field">
                <Label>Email Design</Label>
                <EmailDesignSelect
                  value={formData.email_design_id}
                  onChange={(v) => setFormData({...formData, email_design_id: v})}
                  designs={emailDesigns}
                  testId="template-email-design-select"
                />
                <p className="text-xs text-muted-foreground">The template body replaces the design's Email Content block when sent.</p>
              </div>
            )}

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="body">Template Content *</Label>
                <div className="flex items-center gap-1">
                  <span className="text-xs text-gray-500">Insert field:</span>
                  <Select onValueChange={(value) => insertMergeField(value)}>
                    <SelectTrigger className="w-48 h-7 text-xs">
                      <SelectValue placeholder="Merge fields" />
                    </SelectTrigger>
                    <SelectContent className="max-h-80">
                      {mergeFields.map((mf) => (
                        <SelectItem key={mf.field} value={mf.field} className="text-xs">
                          <span className="font-mono">{mf.field}</span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <Textarea
                id="body"
                ref={bodyTextareaRef}
                value={formData.body}
                onChange={handleBodyChange}
                onSelect={handleBodySelect}
                onClick={handleBodySelect}
                onKeyUp={handleBodySelect}
                placeholder="Enter template content with merge fields like {{participant_full_name}}..."
                rows={8}
                required
                data-testid="template-body-input"
              />
              <div className="text-xs text-muted-foreground mt-1">
                <details>
                  <summary className="cursor-pointer hover:text-primary">View all available merge fields</summary>
                  <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-1 p-2 bg-muted/50 rounded max-h-48 overflow-y-auto">
                    {mergeFields.map((mf) => (
                      <div key={mf.field} className="text-xs">
                        <code className="bg-muted px-1 rounded">{mf.field}</code>
                        <span className="text-muted-foreground ml-1">- {mf.description}</span>
                      </div>
                    ))}
                  </div>
                </details>
              </div>
            </div>

            {formData.category === 'prompts' && (
              <div className="space-y-2">
                <Label htmlFor="ai_prompt">AI System Prompt</Label>
                <Textarea
                  id="ai_prompt"
                  value={formData.ai_prompt}
                  onChange={(e) => setFormData({...formData, ai_prompt: e.target.value})}
                  placeholder="System prompt for AI generation..."
                  rows={4}
                />
                <p className="text-xs text-gray-500">
                  This prompt guides the AI when generating content based on this template
                </p>
              </div>
            )}

            <div className="flex items-center gap-2">
              <Switch
                checked={formData.is_active}
                onCheckedChange={(checked) => setFormData({...formData, is_active: checked})}
              />
              <Label>Active Template</Label>
            </div>

            {formData.category === 'case_notes' && (
              <div className="space-y-2">
                <Label htmlFor="default_note_type">Default Case Note Type (Optional)</Label>
                <Select
                  value={formData.default_note_type || "none"}
                  onValueChange={(val) => setFormData({...formData, default_note_type: val === "none" ? "" : val})}
                >
                  <SelectTrigger data-testid="default-note-type-select">
                    <SelectValue placeholder="Select default note type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No default (user selects)</SelectItem>
                    {noteTypes.map((type) => (
                      <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  When this template is selected, the note type will automatically be set to this value
                </p>
              </div>
            )}

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={saving} data-testid="save-template-btn">
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                {editingTemplate ? 'Update Template' : 'Create Template'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={showPreviewDialog} onOpenChange={setShowPreviewDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{previewTemplate?.name}</DialogTitle>
            <DialogDescription>{previewTemplate?.description}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {previewTemplate?.subject && (
              <div>
                <Label className="text-xs text-gray-500">Subject</Label>
                <p className="font-medium">{previewTemplate.subject}</p>
              </div>
            )}
            <div>
              <Label className="text-xs text-gray-500">Content</Label>
              <div className="mt-2 p-4 bg-gray-50 rounded-lg whitespace-pre-wrap text-sm">
                {previewTemplate?.body}
              </div>
            </div>
            {previewTemplate?.ai_prompt && (
              <div>
                <Label className="text-xs text-gray-500">AI Prompt</Label>
                <div className="mt-2 p-4 bg-blue-50 rounded-lg whitespace-pre-wrap text-sm">
                  {previewTemplate.ai_prompt}
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPreviewDialog(false)}>
              Close
            </Button>
            <Button onClick={() => {
              setShowPreviewDialog(false);
              handleOpenDialog(previewTemplate);
            }}>
              <Edit className="w-4 h-4 mr-2" />
              Edit
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertCircle className="w-5 h-5" />
              Delete Template
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to delete &quot;{deletingTemplate?.name}&quot;? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete} disabled={saving}>
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Delete Template
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TemplatesPage;
